/**
 * Generates deterministic daily stats for the homepage counters.
 * - At the start of each month, "this month" resets to a random 15-30 value.
 * - Each subsequent day adds a random 1-50 to the monthly total.
 * - "Yesterday" shows how much was added on the previous day.
 * - All values are deterministic based on the date (same for all visitors).
 */

function seededRandom(seed: number): number {
  // Simple deterministic hash
  let x = Math.sin(seed * 9301 + 49297) * 49297;
  x = Math.sin(x) * 10000;
  return x - Math.floor(x);
}

function randomInRange(seed: number, min: number, max: number): number {
  return Math.floor(seededRandom(seed) * (max - min + 1)) + min;
}

export function getMonthlyStats(): { thisMonth: number; yesterday: number } {
  // Use UK time (Europe/London)
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/London" })
  );
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = now.getDate();

  // Day 1 base value: random 15-30 seeded by year+month
  const baseSeed = year * 100 + month;
  const dayOneValue = randomInRange(baseSeed, 15, 30);

  if (today === 1) {
    return { thisMonth: dayOneValue, yesterday: dayOneValue };
  }

  // Accumulate daily additions from day 2 to today
  let total = dayOneValue;
  for (let day = 2; day <= today; day++) {
    const daySeed = year * 10000 + month * 100 + day;
    total += randomInRange(daySeed, 1, 50);
  }

  // Yesterday's addition
  const yesterdaySeed = year * 10000 + month * 100 + today;
  const yesterdayValue = randomInRange(yesterdaySeed, 1, 50);

  // Wait — yesterday is the value added on *today's* date (day = today),
  // but "yesterday" label means the previous day's addition.
  // Recalculate: yesterday = addition on (today - 1)
  if (today === 2) {
    // Yesterday was day 1, so yesterday = dayOneValue
    return { thisMonth: total, yesterday: dayOneValue };
  }

  const yesterdayActualSeed = year * 10000 + month * 100 + (today - 1);
  const yesterdayActual = randomInRange(yesterdayActualSeed, 1, 50);

  return { thisMonth: total, yesterday: yesterdayActual };
}
